import UserWithAvatar from "components/Atoms/UserWithAvatar";
import ICalFeedTemplatePaginatedDropdown from "components/dropdowns/paginated/ICalFeedTemplatePaginatedDropdown";
import SelectionStartDropdown, { getSelectionStartDropdownLabel } from "components/dropdowns/reactselect/SelectionStartDropdown";
import TimezoneDropdown from "components/dropdowns/reactselect/TimezoneDropdown";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import { CompanySidePanel } from "components/SidePanel/Company/CompanySidePanel";
import SmallDetails from "components/SmallDetails/SmallDetails";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import moment from "moment";
import React, { useState } from "react";
import { useCustomQuery } from "utilities/hookutils";
import { formatLongDate } from "utilities/momentutils";
import { RecordsCountMessage } from "./RecordsCountMessage";
import { SpecifyTime } from "./SpecifyTime";
import { formatTimeZone, getTimeZoneFromIana } from "shared/utils/dateTimeUtils";
import { useCurrentUser } from "common/SessionContext";
import { findEnumLabel } from "utilities/enumutils";

interface ICalendarFeedDetailSectionProps {
  iCalendarFeed: server.dto.ICalendarFeed
  hasICalendarTemplates: boolean
}

interface ICalendarFeedInfo {
  template: server.dto.ICalendarTemplateSmall
  includeCancelled: boolean
  includeFlight: boolean
  includeOptions: boolean
  selectionStartSubtractDays: number
  ianaTimeZoneId: string;
  cacheHackSetting: server.dto.CacheHackSettings;
}

export const ICalendarFeedDetailSection = (props: ICalendarFeedDetailSectionProps) => {
  const currentUser = useCurrentUser();
  const [iCalendarFeed, setICalendarFeed] = useState<ICalendarFeedInfo>({
    template: props.iCalendarFeed.template,
    includeCancelled: props.iCalendarFeed.includeCancelled,
    includeFlight: props.iCalendarFeed.includeFlight,
    includeOptions: props.iCalendarFeed.includeOptions,
    selectionStartSubtractDays: props.iCalendarFeed.selectionStartSubtractDays,
    ianaTimeZoneId: props.iCalendarFeed.ianaTimeZoneId,
    cacheHackSetting: props.iCalendarFeed.cacheHackSetting,
  });

  const onValueChanged = (key: string, value: any) => {
    if (key === "template") {
      setICalendarFeed({ ...iCalendarFeed, template: value });
    } else if (key === "includeCancelled") {
      setICalendarFeed({ ...iCalendarFeed, includeCancelled: value });
    } else if (key === "includeOptions") {
      setICalendarFeed({ ...iCalendarFeed, includeOptions: value });
    } else if (key === "includeFlights") {
      setICalendarFeed({ ...iCalendarFeed, includeFlight: value });
    } else if (key === "selectionStartSubtractDays") {
      setICalendarFeed({ ...iCalendarFeed, selectionStartSubtractDays: value });
    } else if (key === "ianaTimeZoneId") {
      setICalendarFeed({ ...iCalendarFeed, ianaTimeZoneId: value });
    } else if (key === "previousEvents") {
      setICalendarFeed({ ...iCalendarFeed, selectionStartSubtractDays: value });
    } else if (key === "cacheHackSetting") {
      setICalendarFeed({ ...iCalendarFeed, cacheHackSetting: value });
    }
  }

  const getUpdateICalendarFeedInstruction = (ical: ICalendarFeedInfo): server.dto.UpdateICalendarFeedInstruction => ({
    iCalendarFeedId: props.iCalendarFeed.iCalendarFeedId,
    iCalendarTemplateId: ical.template?.iCalendarTemplateId,
    includeCancelled: ical.includeCancelled,
    includeFlight: ical.includeFlight,
    includeOptions: ical.includeOptions,
    selectionStartSubtractDays: ical.selectionStartSubtractDays,
    ianaTimeZoneId: ical.ianaTimeZoneId,
    cacheHackSetting: ical.cacheHackSetting,
  });

  const isOwner = props.iCalendarFeed.owner.user.userId === currentUser.userId;
  const canEdit = isOwner || site.permissions.canConfigureSystemSettings;

  const recordsQuery = useCustomQuery({
    queryKey: ["iCalendarFeedRecords", props.iCalendarFeed.iCalendarFeedId],
    queryFn: async () => window.CoreApi.ICalendarFeed.GetEventCount(props.iCalendarFeed.iCalendarFeedId).then(r => r.data.data),
  });

  const onSuccessfulUpdate = () => {
    recordsQuery.refetch();
    return Promise.resolve();
  }

  const tz = iCalendarFeed.ianaTimeZoneId ? getTimeZoneFromIana(iCalendarFeed.ianaTimeZoneId, serverReference.culture.uiLocale) : null;

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={onSuccessfulUpdate}
    sections={[{
      type: "regular",
      key: "iCalendarFeed",
      title: props.iCalendarFeed.title,
      fields: [{
        key: "artist",
        label: site.currentEnvironment.functionGroups.artist.singular,
        render: () => <CompanySidePanel type="artist"
          companyId={props.iCalendarFeed.artist.artistId}
          trigger={<a>{props.iCalendarFeed.artist.name}</a>}
        />,
      }, {
        key: "owner",
        label: RESX.Feed.FeedOwner,
        render: () => <UserWithAvatar user={props.iCalendarFeed.owner.user} />
      }, {
        key: "feedUrl",
        label: RESX.Feed.FeedUrl,
        hidden: !isOwner,
        render: () => <a href={props.iCalendarFeed.helpUrl}>{RESX.GeneralLabels.ClickHere}</a>,
      }, {
        key: "template",
        label: RESX.Template.ICalendarTemplate,
        editDisabled: !canEdit,
        hidden: !props.hasICalendarTemplates,
        render: () => <div>{iCalendarFeed.template.title}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.ICalendarTemplateSmall>) => <ICalFeedTemplatePaginatedDropdown
            value={value}
            onChange={(temp) => {
              onChange(temp, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: iCalendarFeed.template,
          submitChange: (template: server.dto.ICalendarTemplateSmall) => window.CoreApi.ICalendarFeed.Update(getUpdateICalendarFeedInstruction({
            ...iCalendarFeed,
            template
          })),
        },
      }, {
        key: "selectionParamsLabel",
        hideLabel: true,
        label: "",
        render: () => <h3>{RESX.Feed.SelectionParameters}</h3>
      }, {
        key: "filterByPulbication",
        label: RESX.Feed.FilterByPublication,
        render: () => <div>
          {props.iCalendarFeed.publicationStatuses.map(status => <div key={status}>{findEnumLabel(status, serverReference.publicationStatuses)}</div>)}
        </div>,
      }, {
        key: "previousEvents",
        editDisabled: !canEdit,
        label: RESX.Feed.IncludePreviousEvents,
        render: () => <SmallDetails
          label={getSelectionStartDropdownLabel(iCalendarFeed.selectionStartSubtractDays)}
          subLabel={formatLongDate(moment.utc().subtract(iCalendarFeed.selectionStartSubtractDays, "days"))}
        />,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <SelectionStartDropdown
            value={value}
            onChange={(start) => {
              onChange(start, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: iCalendarFeed.selectionStartSubtractDays,
          submitChange: (selectionStartSubtractDays: number) => window.CoreApi.ICalendarFeed.Update(getUpdateICalendarFeedInstruction({
            ...iCalendarFeed,
            selectionStartSubtractDays
          })),
        },
      }, {
        key: "includeConfirmed",
        label: RESX.Status.IncludeConfirmed,
        render: () => <YesNoText value={true} yesLabel={RESX.GeneralLabels.Always} />,
      }, {
        key: "includeOptions",
        editDisabled: !canEdit,
        label: RESX.Status.IncludeOptions,
        render: () => <YesNoText value={iCalendarFeed.includeOptions} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: iCalendarFeed.includeOptions,
          submitChange: (includeOptions: boolean) => window.CoreApi.ICalendarFeed.Update(getUpdateICalendarFeedInstruction({
            ...iCalendarFeed,
            includeOptions
          })),
        }
      }, {
        key: "includeCancelled",
        editDisabled: !canEdit,
        label: RESX.Status.IncludeCancelled,
        render: () => <YesNoText value={iCalendarFeed.includeCancelled} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: iCalendarFeed.includeCancelled,
          submitChange: (includeCancelled: boolean) => window.CoreApi.ICalendarFeed.Update(getUpdateICalendarFeedInstruction({
            ...iCalendarFeed,
            includeCancelled
          })),
        },
      }, {
        key: "includeFlights",
        editDisabled: !canEdit,
        label: RESX.Feed.IncludeFlights,
        render: () => <YesNoText value={iCalendarFeed.includeFlight} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: iCalendarFeed.includeFlight,
          submitChange: (includeFlight: boolean) => window.CoreApi.ICalendarFeed.Update(getUpdateICalendarFeedInstruction({
            ...iCalendarFeed,
            includeFlight
          })),
        },
      }, {
        key: "itemsInFeed",
        label: RESX.Feed.ItemsInFeed,
        render: () => <RecordsCountMessage
          isLoading={recordsQuery.isLoading}
          count={recordsQuery.data}
        />
      }, {
        key: "informationLabel",
        hideLabel: true,
        label: "",
        render: () => <h3>{RESX.GeneralLabels.Information}</h3>
      }, {
        key: "cacheDuration",
        label: RESX.Feed.CacheDuration,
        render: () => <div>{RESX.Feed.CacheDurationMessage}</div>,
      }, {
        key: "cacheHackSetting",
        editDisabled: !canEdit,
        label: "Trick external cache",
        render: () => <SmallDetails
          label={iCalendarFeed.cacheHackSetting === "addDateToEveryAppointment" ? RESX.GeneralLabels.lblYes : RESX.GeneralLabels.lblNo}
          subLabel={"Add \"Refreshed from SystemOne on {date}\" with each appointment in the feed. This can be useful when subscribed readers like Google Calendar aren't fetching the feeds at least daily."}
        />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<server.dto.CacheHackSettings>) => <YesNoRadioGroupForInPageEditing
            value={value === "addDateToEveryAppointment"}
            onChange={(v) => onChange(v ? "addDateToEveryAppointment" : "off", true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: iCalendarFeed.cacheHackSetting,
          submitChange: (cacheHackSetting: server.dto.CacheHackSettings) => window.CoreApi.ICalendarFeed.Update(getUpdateICalendarFeedInstruction({
            ...iCalendarFeed,
            cacheHackSetting
          })),
        },
      }, {
        key: "dangerZoneLabel",
        hideLabel: true,
        valueClassName: "icalendar-feed-detail-dangerzone-header",
        label: "",
        render: () => <h3 className="s1_red">{RESX.Feed.DangerZone}</h3>
      }, {
        key: "disclaimer",
        label: RESX.Feed.Disclaimer,
        labelClassName: "icalendar-feed-detail-dangerzone",
        valueClassName: "icalendar-feed-detail-dangerzone",
        render: () => <div>
          Appointments are displayed as &quot;all day events&quot; in SystemOne iCalendar feeds. By specifying a start- and end time of appointments you run the risk that events shift in time because of timezone issues, especially when traveling.
          <br />
          By using this feature, you accept the risk of appointments being displayed on a different time than you&apos;d expect.
          <br />
          If you are unsure of what you are doing don&apos;t use this feature.
        </div>,
      }, {
        key: "ianaTimeZoneId",
        label: RESX.DateTime.Timezone,
        labelClassName: "icalendar-feed-detail-dangerzone",
        valueClassName: "icalendar-feed-detail-dangerzone",
        hidden: !iCalendarFeed.ianaTimeZoneId,
        render: () => <SmallDetails
          label={formatTimeZone(tz)}
          subLabel={RESX.Feed.AssumeAppointmentsHappenInTimeZone}
        />,
        inPageEdit: {
          render: ({ value, onChange, cancel }) => <TimezoneDropdown
            value={value}
            onChange={v => onChange(v, true)}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true
            }}
          />,
          type: "custom",
          initialValue: iCalendarFeed.ianaTimeZoneId,
          fullWidth: true,
          submitChange: (ianaTimeZoneId: string) => window.CoreApi.ICalendarFeed.Update(getUpdateICalendarFeedInstruction({
            ...iCalendarFeed,
            ianaTimeZoneId
          })),
        }
      }, {
        key: "specifyTime",
        label: RESX.Feed.SpecifyTime,
        labelClassName: "icalendar-feed-detail-dangerzone-end-label",
        valueClassName: "icalendar-feed-detail-dangerzone-end-value",
        render: () => <SpecifyTime
          timeZone={iCalendarFeed.ianaTimeZoneId}
          setTimeZone={async (tz) => {
            const result = await window.CoreApi.ICalendarFeed.Update(getUpdateICalendarFeedInstruction({
              ...iCalendarFeed,
              ianaTimeZoneId: tz,
            }));
            if (!result.data.hasErrors) {
              setICalendarFeed({ ...iCalendarFeed, ianaTimeZoneId: tz })
            }
            return result;
          }}
        />
      }]
    }]}
  />
}
